import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SelectedProduct {
  productoId: string;
  nombre: string;
  descripcion?: string;
  precio: number;
  cantidad: number;
  imagen?: string;
  sku?: string;
}

export interface SelectedPackage {
  packageId: string;
  nombre: string;
  descripcion?: string;
  precio: number;
  cantidad: number;
  imagen?: string;
}

interface ProductSelectionState {
  selectedProducts: SelectedProduct[];
  selectedPackages: SelectedPackage[];
  empresaSlug: string | null;
  savedAt: number | null;

  addProduct: (product: Omit<SelectedProduct, 'cantidad'> & { cantidad?: number }) => void;
  removeProduct: (productoId: string) => void;
  updateQuantity: (productoId: string, cantidad: number) => void;
  addPackage: (pkg: Omit<SelectedPackage, 'cantidad'> & { cantidad?: number }) => void;
  removePackage: (packageId: string) => void;
  updatePackageQuantity: (packageId: string, cantidad: number) => void;
  clearSelection: () => void;
  setEmpresaSlug: (slug: string) => void;

  isProductSelected: (productoId: string) => boolean;
  isPackageSelected: (packageId: string) => boolean;
  getTotalItems: () => number;
  getTotalValue: () => number;
}

const EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24h

export const useProductSelectionStore = create<ProductSelectionState>()(
  persist(
    (set, get) => {
      const touch = () => set({ savedAt: Date.now() });

      return {
        selectedProducts: [],
        selectedPackages: [],
        empresaSlug: null,
        savedAt: null,

        addProduct: (product) => {
          const existing = get().selectedProducts.find((p) => p.productoId === product.productoId);
          if (existing) return;
          set((state) => ({
            selectedProducts: [
              ...state.selectedProducts,
              { ...product, cantidad: product.cantidad ?? 1 },
            ],
          }));
          touch();
        },

        removeProduct: (productoId) => {
          set((state) => ({
            selectedProducts: state.selectedProducts.filter((p) => p.productoId !== productoId),
          }));
          touch();
        },

        updateQuantity: (productoId, cantidad) => {
          if (cantidad <= 0) {
            get().removeProduct(productoId);
            return;
          }
          set((state) => ({
            selectedProducts: state.selectedProducts.map((p) =>
              p.productoId === productoId ? { ...p, cantidad } : p
            ),
          }));
          touch();
        },

        addPackage: (pkg) => {
          const existing = get().selectedPackages.find((p) => p.packageId === pkg.packageId);
          if (existing) return;
          set((state) => ({
            selectedPackages: [
              ...state.selectedPackages,
              { ...pkg, cantidad: pkg.cantidad ?? 1 },
            ],
          }));
          touch();
        },

        removePackage: (packageId) => {
          set((state) => ({
            selectedPackages: state.selectedPackages.filter((p) => p.packageId !== packageId),
          }));
          touch();
        },

        updatePackageQuantity: (packageId, cantidad) => {
          if (cantidad <= 0) {
            get().removePackage(packageId);
            return;
          }
          set((state) => ({
            selectedPackages: state.selectedPackages.map((p) =>
              p.packageId === packageId ? { ...p, cantidad } : p
            ),
          }));
          touch();
        },

        clearSelection: () => {
          set({ selectedProducts: [], selectedPackages: [], empresaSlug: null, savedAt: null });
        },

        setEmpresaSlug: (slug: string) => {
          const current = get().empresaSlug;
          // If user switches companies, clear previous selection
          if (current && current !== slug) {
            set({ selectedProducts: [], selectedPackages: [], empresaSlug: slug, savedAt: Date.now() });
            return;
          }
          set({ empresaSlug: slug });
          touch();
        },

        isProductSelected: (productoId) => get().selectedProducts.some((p) => p.productoId === productoId),

        isPackageSelected: (packageId) => get().selectedPackages.some((p) => p.packageId === packageId),

        getTotalItems: () =>
          get().selectedProducts.reduce((sum, p) => sum + (p.cantidad || 0), 0) +
          get().selectedPackages.reduce((sum, p) => sum + (p.cantidad || 0), 0),

        getTotalValue: () =>
          get().selectedProducts.reduce((sum, p) => sum + (p.precio || 0) * (p.cantidad || 0), 0) +
          get().selectedPackages.reduce((sum, p) => sum + (p.precio || 0) * (p.cantidad || 0), 0),
      };
    },
    {
      name: 'product-selection-storage',
      partialize: (state) => ({
        selectedProducts: state.selectedProducts,
        selectedPackages: state.selectedPackages,
        empresaSlug: state.empresaSlug,
        savedAt: state.savedAt,
      }),
      onRehydrateStorage: () => (state, error) => {
        if (error || !state) return;
        if (!state.savedAt) return;
        const expired = Date.now() - state.savedAt > EXPIRATION_MS;
        if (expired) {
          state.clearSelection();
        }
      },
    }
  )
);


